import { useId, type ReactNode } from 'react';

/**
 * The resolved id + ARIA wiring FormField threads down to the control it
 * wraps, via the render-prop form of `children` (see FormFieldProps below).
 */
export interface FormFieldControlProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': 'true' | undefined;
}

export interface FormFieldProps {
  /** Label text. When omitted, no <label> is rendered (and no required marker). */
  label?: string;
  /** The id the label points at / the control should use. Auto-generated when omitted. */
  htmlFor?: string;
  /** Renders a `*` (aria-hidden) beside the label when true. */
  required?: boolean;
  /** Help text rendered below the control; wired into the control's aria-describedby. */
  help?: string;
  /** Error text rendered below the control; sets aria-invalid and extends aria-describedby. */
  error?: string;
  /**
   * The control. Either a plain node (for callers that manage their own
   * id/aria wiring), or a render-prop function that receives the resolved
   * `{ id, aria-describedby, aria-invalid }` to spread onto the control.
   */
  children: ReactNode | ((controlProps: FormFieldControlProps) => ReactNode);
}

/**
 * FormField — the base wrapper every form control composes for consistent
 * label / required-marker / help / error chrome (BR-UI-FF-001..006).
 *
 * Threading approach (BR-UI-FF-005): render-prop, not context. FormField
 * wraps exactly one control per instance, so there's no need for a Provider
 * spanning multiple descendants — a render-prop keeps the id/aria wiring
 * visible at the call site (`{(controlProps) => <input {...controlProps} />}`)
 * rather than implicit through a paired hook. A plain-ReactNode fallback is
 * also accepted for callers that already manage their own id/aria wiring;
 * no threading happens in that case (the render-prop path is the tested one).
 */
export function FormField({ label, htmlFor, required, help, error, children }: FormFieldProps) {
  const generatedId = useId();
  const controlId = htmlFor || (label ? label.toLowerCase().replace(/\s+/g, '-') : generatedId);
  const helpId = help ? `${controlId}-help` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  const controlProps: FormFieldControlProps = {
    id: controlId,
    'aria-describedby': describedBy,
    'aria-invalid': error ? 'true' : undefined,
  };

  return (
    <div>
      {label && (
        <label htmlFor={controlId} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && (
            <span aria-hidden="true" className="text-red-600 ml-0.5">
              *
            </span>
          )}
        </label>
      )}
      {typeof children === 'function' ? children(controlProps) : children}
      {help && (
        <p id={helpId} className="text-sm text-gray-500">
          {help}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-sm text-red-600 mt-1">
          {error}
        </p>
      )}
    </div>
  );
}
